import { Suspense } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingSocialIcons from '../../components/base/FloatingSocialIcons';

const AboutPage = () => {
  const values = [
    {
      icon: 'ri-heart-fill',
      title: 'Client-First Approach',
      description: 'Your success is our success. We treat your business like our own and are invested in your growth.'
    },
    {
      icon: 'ri-lightbulb-fill',
      title: 'Creative Excellence',
      description: 'We don\'t do cookie-cutter content. Every piece is custom-created to showcase your unique brand.'
    },
    {
      icon: 'ri-shield-check-fill',
      title: 'Transparency & Trust',
      description: 'No hidden fees, no long contracts, no BS. Just honest service and real results.'
    },
    {
      icon: 'ri-rocket-fill',
      title: 'Results-Driven',
      description: 'Pretty pictures are nice, but we focus on content that generates leads and grows your business.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50 relative overflow-hidden">
      <FloatingSocialIcons />
      <Header />
      
      <main className="relative z-10">
        {/* Hero Section */}
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              About Digital Experience Studio
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              We're a team of passionate creatives dedicated to helping small businesses succeed through powerful social media content marketing.
            </p>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
                  Our Story
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Digital Experience Studio was born from a simple observation: small businesses were being left behind in the social media revolution. While big brands had entire teams and massive budgets, small business owners were struggling to create quality content while running their businesses.
                  </p>
                  <p>
                    We knew there had to be a better way. In 2019, we started Digital Experience Studio with a mission to provide enterprise-level social media marketing at prices small businesses could afford.
                  </p>
                  <p>
                    Today, we've helped over 50 small businesses transform their social media presence, generate thousands of leads, and achieve real, measurable growth. We're not just a service provider - we're your partner in success.
                  </p>
                </div>
              </div>
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/51227bfa87822151e9dafa34dd17eab2.jpg"
                  alt="Our Creative Process"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-16 px-6 bg-white/60 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              Our Core Values
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 rounded-full mx-auto mb-4">
                    <i className={`${value.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-800">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              Our Impact in Numbers
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { number: '50+', label: 'Happy Clients' },
                { number: '2,500+', label: 'Content Pieces Created' },
                { number: '150K+', label: 'Engagement Generated' },
                { number: '95%', label: 'Client Satisfaction' }
              ].map((stat, index) => (
                <div key={index} className="text-center bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                  <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Link Section */}
        <section className="py-16 px-6 bg-white/60 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              Have Questions?
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Check out our comprehensive FAQ section for answers to common questions about our services, pricing, and process.
            </p>
            <Link
              to="/faqs"
              className="inline-block bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              View FAQs
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 rounded-3xl p-12 text-center shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Work Together?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Let's create something amazing for your business. Book your free consultation today!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/consultation"
                className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Free Consultation
              </Link>
              <Link
                to="/quote"
                className="bg-cyan-400 text-blue-800 px-8 py-4 rounded-full font-bold text-lg hover:bg-cyan-300 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Request a Quote
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
